/**
 * Utility functions for parent-child quantity conversion using backend-provided child quantities
 */

import { convertParentChild } from "../components/Constants";

/**
 * Gets the appropriate quantity value (child or parent) based on conversion toggle
 * @param {number} parentQuantity - Original parent quantity
 * @param {number} childQuantity - Backend-provided child quantity
 * @returns {number} - Child quantity if conversion enabled, parent quantity otherwise
 */
export const getDisplayQuantity = (parentQuantity, childQuantity) => {
  if (convertParentChild === 1 && childQuantity !== undefined && childQuantity !== null) {
    return childQuantity;
  }
  return parentQuantity || 0;
};

/**
 * Gets summary quantities from poItemSummary based on conversion toggle
 * @param {object} summary - Summary object from backend
 * @param {string} parentField - Parent quantity field name (e.g., 'totalQuantityConfirmed')
 * @param {string} childField - Child quantity field name (e.g., 'totalChildQuantityConfirmed') 
 * @returns {number} - Appropriate quantity based on conversion setting
 */
export const getSummaryQuantity = (summary, parentField, childField) => {
  if (convertParentChild === 1 && summary[childField] !== undefined) {
    return summary[childField];
  }
  return summary[parentField] || 0;
};

/**
 * Gets temp zone quantities from summary based on conversion toggle
 * @param {object} tempZoneData - Temp zone data from summaryByTempZone
 * @param {string} parentField - Parent quantity field name
 * @param {string} childField - Child quantity field name
 * @returns {number} - Appropriate quantity based on conversion setting
 */
export const getTempZoneQuantity = (tempZoneData, parentField, childField) => {
  if (convertParentChild === 1 && tempZoneData[childField] !== undefined) {
    return tempZoneData[childField];
  }
  return tempZoneData[parentField] || 0;
};

/**
 * Gets the appropriate child ASIN value
 * @param {object} item - Item data
 * @returns {string|null} - Child ASIN if it exists, otherwise null for transshipments or when no child ASIN available
 */
export const getChildAsin = (item) => {
  // For manifest loads, child ASIN should always be NULL/blank
  if (item.loadType === 'Transshipment' || item.source === 'Transshipment') {
    return null;
  }
  
  // Return the child ASIN if it exists (regardless of conversion toggle for display purposes)
  // The backend should provide 'no child asin' or similar when there's no parent-child relationship
  if (item.child_asin) {
    return item.child_asin;
  }
  
  return null;
};

/**
 * Determines if NYR/NYS units should be calculated based on child quantities
 * @param {object} item - Item data
 * @returns {boolean} - Whether to use child quantity calculations
 */
export const shouldUseChildQuantityForNYR = (item) => {
  return convertParentChild === 0;
};
